import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);

    try {
      await axios.post(`${API}/subscribe`, { email });
      setSubscribed(true);
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="contact-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="contact-title">
              Get In Touch
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              We'd love to hear from you. Reach out to us anytime!
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information & Newsletter */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="glass-morphic rounded-2xl p-8 space-y-6">
                <h2 className="text-2xl font-semibold mb-6">Contact Information</h2>

                <div className="space-y-6">
                  <div className="flex items-start gap-4" data-testid="phone-section">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Phone</h3>
                      <a href="tel:8553552031" className="text-muted-foreground hover:text-primary transition-colors duration-300 block">
                        8553552031
                      </a>
                      <a href="tel:9148452031" className="text-muted-foreground hover:text-primary transition-colors duration-300 block">
                        9148452031
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="email-section">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Email</h3>
                      <a 
                        href="mailto:ved3dcreations@gmail.com" 
                        className="text-muted-foreground hover:text-primary transition-colors duration-300"
                      >
                        ved3dcreations@gmail.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/10">
                  <h3 className="font-semibold mb-3">Business Hours</h3>
                  <div className="text-muted-foreground space-y-1">
                    <p>Monday - Saturday: 9:00 AM - 6:00 PM</p>
                    <p>Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Newsletter Subscription moved here */}
              <div className="glass-morphic rounded-2xl p-8">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Send className="h-5 w-5 text-primary" />
                  Stay Updated
                </h2>
                {subscribed ? (
                  <div className="text-center py-4" data-testid="subscription-success">
                    <CheckCircle className="h-10 w-10 text-accent mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Thank you for subscribing!</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="space-y-3" data-testid="subscription-form">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="bg-white/5 border-white/10"
                    />
                    <Button
                      type="submit"
                      disabled={subscribing}
                      className="w-full rounded-full bg-primary hover:bg-primary/90 text-white"
                    >
                      {subscribing ? 'Subscribing...' : 'Subscribe'}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Message Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="lg:col-span-2"
            >
              <div className="glass-morphic rounded-2xl p-8">
                <h2 className="text-2xl font-semibold mb-6">Send us a Message</h2>
                <form onSubmit={handleMessageSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        required
                        className="bg-white/5 border-white/10 h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@example.com"
                        required
                        className="bg-white/5 border-white/10 h-12"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="How can we help?"
                      required
                      className="bg-white/5 border-white/10 h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us more about your inquiry..."
                      required
                      className="bg-white/5 border-white/10 min-h-[150px] resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full md:w-auto px-12 rounded-full bg-primary hover:bg-primary/90 text-white py-6 text-lg shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all duration-300"
                  >
                    {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>

              <div className="mt-8 glass-morphic rounded-2xl p-8">
                <h3 className="text-xl font-semibold mb-4">Why Choose <span className="animate-gradient-text">Ved3dCreations</span>?</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>High-quality 3D printed products</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Custom designs available</span>
                    </li>
                  </ul>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Fast and reliable shipping</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Excellent customer support</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
